import React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from './button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './dialog';

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** Texte ou nœud React — jamais du HTML brut ici (les noms saisis par
      l'utilisateur y passent). `ConfirmActionDialog` gère le cas balisé. */
  message: React.ReactNode;
  /** Seconde ligne, en gris : ce que l'action fait exactement. */
  description?: string;
  /** Libellé du bouton d'action — `common.confirm` par défaut. */
  confirmLabel?: string;
  destructive?: boolean;
  /** Action en cours : les deux boutons se désactivent, le confirm montre un spinner. */
  loading?: boolean;
  onConfirm: () => void;
}

/**
 * Confirmation générique — l'UNIQUE remplaçant de `window.confirm` dans l'app.
 *
 * Dialog React et non `window.confirm` : Safari bloquait le confirm natif et
 * les boutons restaient sans effet (correctif 2026-01-09) ; et un confirm natif
 * est hors thème, bloquant, et non traduisible.
 */
const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open, onOpenChange, title, message, description, confirmLabel, destructive, loading, onConfirm,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!loading) onOpenChange(o); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className={destructive ? 'text-destructive' : undefined}>{title}</DialogTitle>
        </DialogHeader>
        <div className="py-4 space-y-2">
          <p className="text-sm whitespace-pre-line">{message}</p>
          {description && <p className="text-sm text-muted-foreground whitespace-pre-line">{description}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button variant={destructive ? 'destructive' : 'default'} onClick={onConfirm} disabled={loading}>
            {loading && <Loader2 className="animate-spin" />}
            {confirmLabel ?? t('common.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmDialog;
